import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, X, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage
      });

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button with Curved Text */}
      {!isOpen && (
        <div className="fixed bottom-8 right-8 z-50" data-testid="chatbot-container">
          {/* SVG with curved text around the button */}
          <svg
            width="120"
            height="120"
            viewBox="0 0 120 120"
            className="absolute -top-6 -left-6 pointer-events-none"
          >
            <defs>
              <path
                id="curvedTextPath"
                d="M 60,60 m -42,0 a 42,42 0 1,1 84,0 a 42,42 0 1,1 -84,0"
                fill="none"
              />
            </defs>
            <text
              fill="#f97316"
              fontSize="11"
              fontWeight="bold"
              fontFamily="Rajdhani, sans-serif"
              letterSpacing="2"
            >
              <textPath
                href="#curvedTextPath"
                startOffset="25%"
                textAnchor="middle"
              >
                ASK ANY QUESTION
              </textPath>
            </text>
          </svg>
          
          <button
            onClick={() => setIsOpen(true)}
            className="relative bg-orange-500 hover:bg-orange-600 p-2 rounded-full shadow-lg pulse-glow transition-colors"
            data-testid="chatbot-toggle"
            aria-label="Open chatbot"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_steel-build-pro/artifacts/imjdxz67_3d-metal-home-building-logo%202.jpg"
              alt="Chat"
              className="w-12 h-12 rounded-full object-cover"
            />
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-8 right-8 z-50 w-96 h-[600px] flex flex-col glass-card shadow-2xl"
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="bg-orange-500 text-slate-950 p-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MessageSquare size={24} />
              <div>
                <h3 className="font-bold" style={{ fontFamily: 'Rajdhani, sans-serif' }}>3D METAL ASSISTANT</h3>
                <p className="text-xs">Ask us anything about steel building</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-orange-600 p-1 rounded transition-colors"
              data-testid="chatbot-close"
              aria-label="Close chatbot"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-950" data-testid="chatbot-messages">
            {messages.length === 0 && (
              <div className="text-center text-slate-400 mt-8">
                <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-xl font-bold text-orange-500 mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>ASK ANY QUESTIONS</p>
                <p>Hi! I'm here to answer your questions about 3D Metal Home Building.</p>
                <p className="text-sm mt-2">Ask me about steel framing, pricing, or our services!</p>
              </div>
            )}
            
            {messages.map((msg, index) => (
              <div
                key={`${msg.role}-${index}-${msg.content.slice(0, 20)}`}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-message-${msg.role}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-orange-500 text-slate-950'
                      : 'bg-slate-800 text-slate-100 border border-slate-700'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 text-slate-100 border border-slate-700 p-3 rounded-lg">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-slate-900 border-t border-slate-800">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your question..."
                className="flex-1 px-4 py-2 bg-slate-950 border border-slate-800 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 text-white rounded-none placeholder:text-slate-600"
                disabled={isLoading}
                data-testid="chatbot-input"
              />
              <button
                onClick={handleSend}
                disabled={isLoading || !inputMessage.trim()}
                className="bg-orange-500 hover:bg-orange-600 text-slate-950 p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="chatbot-send"
                aria-label="Send message"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
